import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, moneyCompact, num, pct, times } from "@owners/format";
import { api, useMe } from "../api";
import { Tile, useHotels } from "../components/Numbers";

/**
 * Debt Compliance (SPEC §9): loan abstracts per hotel and covenant tests.
 * DSCR and debt yield are calculated here from the books (trailing 12 months);
 * HMA Users never upload them. Asset Managers (and Admins) enter abstracts.
 */

type Status = "ok" | "watch" | "below" | "no_data";
interface Test {
  id: string; kind: "dscr" | "debt_yield" | "other"; label: string; sectionRef: string | null; requirement: number;
  comparator: "min" | "max"; frequency: string; appliesTo: "ongoing" | "extension" | "cash_trap"; value: number | null;
  status: Status; shortfall: number | null; notes: string | null;
}
interface Loan {
  id: string; hotelId: string; lender: string; name: string | null; originalAmount: number | null; balance: number; balanceAsOf: string | null;
  rateType: "fixed" | "floating"; interestRate: number | null; rateIndex: string | null; annualDebtService: number; ffeReservePct: number;
  maturityDate: string | null; daysToMaturity: number | null; extensionOption: string | null; extensionDeadline: string | null;
  nextStep: string | null; notes: string | null; dscr: number | null; debtYield: number | null; lenderNoi: number | null;
  tests: Test[]; status: Status; extensionStatus: Status | null;
}
interface DebtRes {
  through: string | null;
  hotels: Array<{ id: string; name: string; t12: { noi: number | null; revenue: number | null; complete: boolean } | null }>;
  loans: Loan[];
  summary: { loans: number; totalDebt: number; belowCovenant: number; debtBelowCovenant: number; watch: number; maturing12: number; debtMaturing24: number; portfolioDscr: number | null };
  canEdit: boolean;
}

const STATUS: Record<Status, { label: string; cls: string }> = {
  ok: { label: "In compliance", cls: "ok-pill" }, watch: { label: "Watch", cls: "warn-pill" },
  below: { label: "Below covenant", cls: "bad-pill" }, no_data: { label: "Needs 12 months of books", cls: "" },
};
const Pill = ({ s }: { s: Status }) => <span className={`pill ${STATUS[s].cls}`}>{STATUS[s].label}</span>;
const monthLabel = (p: string) => new Date(p + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const day = (iso: string | null) => (iso ? new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "—");

export function fmtTest(kind: Test["kind"], v: number | null): string {
  if (v === null) return "—";
  return kind === "dscr" ? times(v) : kind === "debt_yield" ? pct(v, { ratio: true }) : num(v, { decimals: Math.abs(v) < 10 ? 2 : 0 });
}

/** Balance-weighted rate (percent, as entered) and fixed share — demo KPIs. */
export function debtMix(loans: Array<Pick<Loan, "balance" | "interestRate" | "rateType">>) {
  const total = loans.reduce((a, l) => a + l.balance, 0);
  const rated = loans.filter((l) => l.interestRate !== null);
  const ratedBal = rated.reduce((a, l) => a + l.balance, 0);
  return {
    weightedRate: ratedBal ? rated.reduce((a, l) => a + l.balance * l.interestRate!, 0) / ratedBal : null,
    ratedShare: total ? ratedBal / total : 0,
    fixedShare: total ? loans.filter((l) => l.rateType === "fixed").reduce((a, l) => a + l.balance, 0) / total : 0,
  };
}

/** Plain-English summary under the title (templated, no AI). */
function debtNarrative(d: DebtRes, mix: ReturnType<typeof debtMix>): string {
  const s = d.summary;
  if (!s.loans) return "No loans have been entered yet.";
  const parts = [`${num(s.loans)} loan${s.loans === 1 ? "" : "s"} totaling ${moneyCompact(s.totalDebt)}`
    + (mix.weightedRate !== null ? ` at a weighted ${pct(mix.weightedRate, { decimals: 2 })}` : "")
    + `, ${pct(mix.fixedShare * 100, { decimals: 0 })} fixed.`];
  if (s.belowCovenant) parts.push(`${num(s.belowCovenant)} ${s.belowCovenant === 1 ? "is" : "are"} below covenant (${moneyCompact(s.debtBelowCovenant)} of debt).`);
  else if (s.watch) parts.push(`All pass their tests; ${num(s.watch)} ${s.watch === 1 ? "is" : "are"} close enough to watch.`);
  else parts.push("Every loan passes its covenant tests.");
  if (s.maturing12) parts.push(`${num(s.maturing12)} mature${s.maturing12 === 1 ? "s" : ""} in the next 12 months.`);
  return parts.join(" ");
}

export function DebtCompliance() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["debt"], queryFn: () => api<DebtRes>("/api/debt") });
  const [open, setOpen] = useState<string | null>(null);
  const [popup, setPopup] = useState<"below" | "maturing" | null>(null);
  const [editLoan, setEditLoan] = useState<Partial<Loan> | null>(null);
  const [editCov, setEditCov] = useState<{ loanId: string; cov: Partial<Test> } | null>(null);
  const base = me?.level === "admin" ? "/api/admin/debt" : "/api/debt";
  const refresh = () => { qc.invalidateQueries({ queryKey: ["debt"] }); qc.invalidateQueries({ queryKey: ["today-numbers"] }); };

  const d = q.data;
  const hotelName = (id: string) => d?.hotels.find((h) => h.id === id)?.name ?? "";
  const s = d?.summary;
  const mix = d ? debtMix(d.loans) : null;

  return (
    <>
      <div className="eyebrow">Owners Module · Debt Compliance</div>
      <h1>Loans &amp; covenants</h1>
      {d?.through && <p className="muted" style={{ marginTop: -6 }}>Tests use the trailing 12 months of books through {monthLabel(d.through)}.</p>}

      {q.isLoading ? <div className="card muted">Loading…</div> : q.error ? <div className="card error">{(q.error as Error).message}</div> : d && (
        <>
          <p className="narrative">{debtNarrative(d, mix!)}</p>
          <div className="tiles strip">
            <Tile label="Total debt" value={moneyCompact(s!.totalDebt)} sub={`${num(s!.loans)} loans`} />
            <Tile label="Below covenant" value={num(s!.belowCovenant)} sub={s!.belowCovenant ? `${moneyCompact(s!.debtBelowCovenant)} of debt` : s!.watch ? `${num(s!.watch)} on watch` : "All loans pass"}
              onClick={s!.belowCovenant || s!.watch ? () => setPopup("below") : undefined} />
            <Tile label="Maturing in 12 months" value={num(s!.maturing12)} sub={`${moneyCompact(s!.debtMaturing24)} matures within 24 months`}
              onClick={s!.maturing12 ? () => setPopup("maturing") : undefined} />
            <Tile label="Weighted rate" value={mix!.weightedRate !== null ? pct(mix!.weightedRate, { decimals: 2 }) : "—"}
              sub={mix!.ratedShare < 1 && mix!.weightedRate !== null ? `${pct(mix!.ratedShare * 100, { decimals: 0 })} of debt has a rate entered` : "Weighted by balance"} />
            <Tile label="Fixed rate" value={s!.totalDebt ? pct(mix!.fixedShare * 100, { decimals: 0 }) : "—"} sub="Share of debt; the rest floats" />
            <Tile label="Portfolio DSCR" value={times(s!.portfolioDscr)} sub="Weighted by debt service" />
          </div>

          <div className="card">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ margin: 0 }}>Loan abstracts</h2>
              {d.canEdit && <button className="btn" onClick={() => setEditLoan({ rateType: "fixed", ffeReservePct: 0.04 })}>+ Add loan</button>}
            </div>
            {d.loans.length === 0 ? (
              <p className="muted">No loans entered yet.{d.canEdit ? " Add each hotel's loan from its loan agreement — covenants are calculated from the books." : ""}</p>
            ) : (
              <table className="fin">
                <thead><tr><th>Hotel</th><th>Lender</th><th className="num">Balance</th><th className="num">Rate</th><th>Maturity</th>
                  <th className="num">DSCR</th><th className="num">Debt yield</th><th>Status</th></tr></thead>
                <tbody>
                  {d.loans.map((l) => (
                    <LoanRows key={l.id} l={l} hotel={hotelName(l.hotelId)} open={open === l.id} onToggle={() => setOpen(open === l.id ? null : l.id)}
                      canEdit={d.canEdit} onEdit={() => setEditLoan(l)} onAddCov={() => setEditCov({ loanId: l.id, cov: { kind: "dscr", comparator: "min", frequency: "quarterly", appliesTo: "ongoing" } })}
                      onEditCov={(c) => setEditCov({ loanId: l.id, cov: c })} />
                  ))}
                </tbody>
              </table>
            )}
            <p className="muted" style={{ fontSize: 12 }}>
              DSCR = Lender NOI ÷ annual debt service, where Lender NOI = NOI − FF&amp;E reserve (4% of revenue unless the loan says otherwise).
              Debt yield = NOI ÷ loan balance. "Watch" means passing by less than 0.10x DSCR or 0.5 points of debt yield.
            </p>
          </div>
        </>
      )}

      {popup && d && (
        <Modal title={popup === "below" ? "Below covenant" : "Maturing in 12 months"} onClose={() => setPopup(null)}>
          <table>
            <thead><tr><th>Hotel</th><th>Lender</th><th className="num">Balance</th><th>{popup === "below" ? "Test" : "Maturity"}</th><th>Next step</th></tr></thead>
            <tbody>
              {(popup === "below" ? d.loans.filter((l) => l.status === "below" || l.status === "watch")
                : d.loans.filter((l) => l.daysToMaturity !== null && l.daysToMaturity <= 365)).map((l) => (
                <tr key={l.id}>
                  <td>{hotelName(l.hotelId)}</td><td>{l.lender}</td><td className="num">{moneyCompact(l.balance)}</td>
                  <td>{popup === "below"
                    ? l.tests.filter((t) => t.appliesTo !== "extension" && (t.status === "below" || t.status === "watch"))
                        .map((t) => `${t.label} ${fmtTest(t.kind, t.value)} vs ${fmtTest(t.kind, t.requirement)}`).join("; ")
                    : `${day(l.maturityDate)} · ${num(l.daysToMaturity)} days`}</td>
                  <td>{l.nextStep ?? <span className="tbd">TBD</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      {editLoan && <LoanEditor base={base} loan={editLoan} onClose={() => setEditLoan(null)} onSaved={() => { setEditLoan(null); refresh(); }} />}
      {editCov && <CovenantEditor base={base} loanId={editCov.loanId} cov={editCov.cov} onClose={() => setEditCov(null)} onSaved={() => { setEditCov(null); refresh(); }} />}
    </>
  );
}

function LoanRows({ l, hotel, open, onToggle, canEdit, onEdit, onAddCov, onEditCov }: {
  l: Loan; hotel: string; open: boolean; onToggle: () => void; canEdit: boolean;
  onEdit: () => void; onAddCov: () => void; onEditCov: (c: Test) => void;
}) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <td>{open ? "▾" : "▸"} {hotel}</td>
        <td>{l.lender}{l.name ? <span className="muted"> · {l.name}</span> : null}</td>
        <td className="num">{money(l.balance)}</td>
        <td className="num">{l.interestRate !== null ? pct(l.interestRate, { decimals: 2 }) : "—"}{l.rateType === "floating" ? " fl." : ""}</td>
        <td>{day(l.maturityDate)}{l.daysToMaturity !== null && <span className="muted"> · {num(l.daysToMaturity)} days</span>}</td>
        <td className="num">{times(l.dscr)}</td>
        <td className="num">{pct(l.debtYield, { ratio: true })}</td>
        <td><Pill s={l.status} />{l.extensionStatus === "below" && <> <span className="pill bad-pill">Extension test fails</span></>}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} style={{ background: "var(--cream)", padding: "12px 16px" }}>
            <div className="row" style={{ gap: 28, alignItems: "flex-start", marginBottom: 10 }}>
              <Fact k="Annual debt service" v={money(l.annualDebtService)} />
              <Fact k="Lender NOI (T12)" v={money(l.lenderNoi)} />
              <Fact k="Rate" v={l.rateIndex ?? (l.interestRate !== null ? pct(l.interestRate, { decimals: 2 }) : "—")} />
              <Fact k="Extension option" v={l.extensionOption ?? "—"} />
              {l.extensionDeadline && <Fact k="Extension deadline" v={day(l.extensionDeadline)} />}
              <Fact k="Next step" v={l.nextStep ?? <span className="tbd">TBD</span>} />
            </div>
            <table>
              <thead><tr><th>Test</th><th>Section</th><th className="num">Requirement</th><th className="num">Current</th><th>Frequency</th><th>Applies</th><th>Status</th>{canEdit && <th />}</tr></thead>
              <tbody>
                {l.tests.length === 0 && <tr><td colSpan={8} className="muted">No covenants entered.</td></tr>}
                {l.tests.map((t) => (
                  <tr key={t.id}>
                    <td>{t.label}</td><td>{t.sectionRef ?? "—"}</td>
                    <td className="num">{t.comparator === "min" ? "≥ " : "≤ "}{fmtTest(t.kind, t.requirement)}</td>
                    <td className="num">{fmtTest(t.kind, t.value)}</td>
                    <td>{t.frequency[0]!.toUpperCase() + t.frequency.slice(1)}</td>
                    <td>{t.appliesTo === "ongoing" ? "Ongoing" : t.appliesTo === "extension" ? "To extend" : "Cash trap"}</td>
                    <td><Pill s={t.status} /></td>
                    {canEdit && <td><button className="btn link" onClick={() => onEditCov(t)}>Edit</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {l.notes && <p className="muted" style={{ fontSize: 12 }}>{l.notes}</p>}
            {canEdit && (
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn secondary" onClick={onEdit}>Edit loan</button>
                <button className="btn secondary" onClick={onAddCov}>+ Covenant</button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const Fact = ({ k, v }: { k: string; v: ReactNode }) => (
  <div><div className="eyebrow">{k}</div><div>{v}</div></div>
);

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" style={{ width: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ margin: 0 }}>{title}</h2><button className="btn link" onClick={onClose}>Close</button></div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="field"><label>{label}</label>{children}</div>;
}

function LoanEditor({ base, loan, onClose, onSaved }: { base: string; loan: Partial<Loan>; onClose: () => void; onSaved: () => void }) {
  const hotels = useHotels().data?.hotels ?? [];
  const [f, setF] = useState<Record<string, any>>({
    hotelId: loan.hotelId ?? "", lender: loan.lender ?? "", name: loan.name ?? "", balance: loan.balance ?? "", balanceAsOf: loan.balanceAsOf ?? "",
    originalAmount: loan.originalAmount ?? "", rateType: loan.rateType ?? "fixed", interestRate: loan.interestRate ?? "", rateIndex: loan.rateIndex ?? "",
    annualDebtService: loan.annualDebtService ?? "", ffeReservePct: ((loan.ffeReservePct ?? 0.04) * 100).toString(),
    maturityDate: loan.maturityDate ?? "", extensionOption: loan.extensionOption ?? "", extensionDeadline: loan.extensionDeadline ?? "",
    nextStep: loan.nextStep ?? "", notes: loan.notes ?? "",
  });
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, ffeReservePct: f.ffeReservePct === "" ? null : Number(f.ffeReservePct) / 100 };
      return loan.id ? api(`${base}/loans/${loan.id}`, { method: "PATCH", body: { ...body, hotelId: undefined } }) : api(`${base}/loans`, { method: "POST", body });
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: () => api(`${base}/loans/${loan.id}`, { method: "PATCH", body: { active: false } }), onSuccess: onSaved });

  return (
    <Modal title={loan.id ? "Edit loan" : "Add loan"} onClose={onClose}>
      {!loan.id && (
        <Field label="Hotel">
          <select value={f.hotelId} onChange={set("hotelId")}><option value="">Pick a hotel…</option>{hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>
        </Field>
      )}
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Lender"><input value={f.lender} onChange={set("lender")} /></Field>
        <Field label="Loan (optional)"><input value={f.name} onChange={set("name")} placeholder="Senior mortgage" /></Field>
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Balance ($)"><input value={f.balance} onChange={set("balance")} inputMode="decimal" /></Field>
        <Field label="Balance as of"><input type="date" value={f.balanceAsOf} onChange={set("balanceAsOf")} /></Field>
        <Field label="Original amount ($)"><input value={f.originalAmount} onChange={set("originalAmount")} inputMode="decimal" /></Field>
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Rate type"><select value={f.rateType} onChange={set("rateType")}><option value="fixed">Fixed</option><option value="floating">Floating</option></select></Field>
        <Field label="All-in rate (%)"><input value={f.interestRate} onChange={set("interestRate")} inputMode="decimal" /></Field>
        {f.rateType === "floating" && <Field label="Index + spread"><input value={f.rateIndex} onChange={set("rateIndex")} placeholder="1M SOFR + 3.25%" /></Field>}
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Annual debt service ($)"><input value={f.annualDebtService} onChange={set("annualDebtService")} inputMode="decimal" /></Field>
        <Field label="FF&E reserve (% of revenue)"><input value={f.ffeReservePct} onChange={set("ffeReservePct")} inputMode="decimal" /></Field>
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Maturity"><input type="date" value={f.maturityDate} onChange={set("maturityDate")} /></Field>
        <Field label="Extension option"><input value={f.extensionOption} onChange={set("extensionOption")} placeholder="One 12-month extension" /></Field>
        <Field label="Extension deadline"><input type="date" value={f.extensionDeadline} onChange={set("extensionDeadline")} /></Field>
      </div>
      <Field label="Next step"><input value={f.nextStep} onChange={set("nextStep")} /></Field>
      <Field label="Notes"><textarea rows={2} value={f.notes} onChange={set("notes")} /></Field>
      {(save.error || remove.error) && <p className="error">{((save.error || remove.error) as Error).message}</p>}
      <div className="row" style={{ justifyContent: "space-between" }}>
        {loan.id ? <button className="btn danger" onClick={() => confirm("Remove this loan from Debt Compliance?") && remove.mutate()}>Remove loan</button> : <span />}
        <div className="row"><button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save"}</button></div>
      </div>
    </Modal>
  );
}

function CovenantEditor({ base, loanId, cov, onClose, onSaved }: { base: string; loanId: string; cov: Partial<Test>; onClose: () => void; onSaved: () => void }) {
  const toInput = (kind: string, v: number | null | undefined) => (v == null ? "" : kind === "debt_yield" ? String(+(v * 100).toFixed(4)) : String(v));
  const [f, setF] = useState<Record<string, any>>({
    kind: cov.kind ?? "dscr", label: cov.kind === "other" ? cov.label ?? "" : "", sectionRef: cov.sectionRef ?? "",
    requirement: toInput(cov.kind ?? "dscr", cov.requirement), comparator: cov.comparator ?? "min", frequency: cov.frequency ?? "quarterly",
    appliesTo: cov.appliesTo ?? "ongoing", currentValue: cov.kind === "other" ? toInput("other", cov.value) : "", notes: cov.notes ?? "",
  });
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const req = f.requirement === "" ? null : Number(f.requirement) / (f.kind === "debt_yield" ? 100 : 1);
      const body = { ...f, requirement: req, label: f.kind === "other" ? f.label : null, currentValue: f.kind === "other" ? f.currentValue : null };
      return cov.id ? api(`${base}/covenants/${cov.id}`, { method: "PATCH", body }) : api(`${base}/loans/${loanId}/covenants`, { method: "POST", body });
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: () => api(`${base}/covenants/${cov.id}`, { method: "DELETE" }), onSuccess: onSaved });
  const unit = f.kind === "dscr" ? "x" : f.kind === "debt_yield" ? "%" : "";

  return (
    <Modal title={cov.id ? "Edit covenant" : "Add covenant"} onClose={onClose}>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Test"><select value={f.kind} onChange={set("kind")}><option value="dscr">DSCR (calculated)</option><option value="debt_yield">Debt yield (calculated)</option><option value="other">Other (entered)</option></select></Field>
        {f.kind === "other" && <Field label="What is tested"><input value={f.label} onChange={set("label")} placeholder="Loan-to-value" /></Field>}
        <Field label="Agreement section"><input value={f.sectionRef} onChange={set("sectionRef")} placeholder="§6.12(a)" /></Field>
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Must be"><select value={f.comparator} onChange={set("comparator")}><option value="min">At least</option><option value="max">At most</option></select></Field>
        <Field label={`Requirement${unit ? ` (${unit})` : ""}`}><input value={f.requirement} onChange={set("requirement")} inputMode="decimal" placeholder={f.kind === "dscr" ? "1.30" : f.kind === "debt_yield" ? "9" : ""} /></Field>
        {f.kind === "other" && <Field label="Current value"><input value={f.currentValue} onChange={set("currentValue")} inputMode="decimal" /></Field>}
      </div>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Field label="Tested"><select value={f.frequency} onChange={set("frequency")}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annually</option></select></Field>
        <Field label="Applies"><select value={f.appliesTo} onChange={set("appliesTo")}><option value="ongoing">Ongoing</option><option value="extension">To extend</option><option value="cash_trap">Cash trap</option></select></Field>
      </div>
      <Field label="Notes"><input value={f.notes} onChange={set("notes")} /></Field>
      {(save.error || remove.error) && <p className="error">{((save.error || remove.error) as Error).message}</p>}
      <div className="row" style={{ justifyContent: "space-between" }}>
        {cov.id ? <button className="btn danger" onClick={() => confirm("Remove this covenant?") && remove.mutate()}>Remove</button> : <span />}
        <div className="row"><button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save"}</button></div>
      </div>
    </Modal>
  );
}
